// ============================================================
// SALT - TOS Gate Component
// Blocking modal for TOS acceptance + age confirmation.
// Renders nothing when the user has already accepted; renders
// an undismissable modal otherwise.
// ============================================================

import React, { useState } from 'react';
import Modal from '../ui/Modal';
import { useAuthContext } from '../../context/AuthContext';
import axiosClient from '../../api/axiosClient';
import '../../styles/auth.css';

// Build the bare-domain URL so legal links work from any subdomain.
const BARE_DOMAIN = (() => {
  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost' || host.endsWith('.localhost')) {
    return `${window.location.protocol}//localhost:${window.location.port}`;
  }
  return `${window.location.protocol}//playsalt.gg`;
})();

export default function TosGate() {
  const { user, logout, updateUser } = useAuthContext();
  const [tosChecked, setTosChecked] = useState(false);
  const [ageChecked, setAgeChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Don't show if no user is logged in
  if (!user) return null;

  // Don't show if user has already accepted current TOS version and confirmed age.
  // The required version comes from the backend (user.currentTosVersion) rather than
  // a hardcoded frontend constant, so a stale cached bundle on one subdomain can't
  // disagree with the backend about which version is current.
  const needsTos = user.tosAcceptedVersion !== user.currentTosVersion;
  const needsAge = !user.ageConfirmed;
  if (!needsTos && !needsAge) return null;

  const canAccept = tosChecked && ageChecked && !isSubmitting;

  const handleAccept = async () => {
    setError('');
    setIsSubmitting(true);

    try {
      const res = await axiosClient.post('/accept-tos', {
        tosAccepted: true,
        ageConfirmed: true,
      });

      if (res.data?.success) {
        const u = res.data.data.user;
        updateUser({
          tosAcceptedVersion: u.tos_accepted_version ?? null,
          currentTosVersion: u.current_tos_version,
          ageConfirmed: u.age_confirmed ?? false,
        });
      } else {
        setError(res.data?.message || 'Failed to accept. Please try again.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = () => {
    logout();
  };

  return (
    <Modal
      isOpen={true}
      title="Terms of Service & Age Confirmation"
      onConfirm={handleAccept}
      onCancel={handleDecline}
      confirmLabel={isSubmitting ? 'Accepting...' : 'Accept'}
      cancelLabel="Decline"
      disableConfirm={!canAccept}
      closeOnBackdrop={false}
      closeOnEsc={false}
    >
      <div>
        <p style={{ fontSize: '0.85rem', color: '#a3a3a3', marginBottom: '16px', lineHeight: 1.5 }}>
          {user.tosAcceptedVersion
            ? "We've made updates to our Terms of Service and Privacy Policy. Please review and accept the new terms to continue using SALT."
            : 'To continue using SALT, please review and accept our terms.'}
        </p>

        <div className="sl-auth-consent">
          <label className="sl-auth-checkbox">
            <input
              type="checkbox"
              checked={tosChecked}
              onChange={(e) => setTosChecked(e.target.checked)}
              disabled={isSubmitting}
            />
            <span className="sl-auth-checkbox-mark" />
            <span className="sl-auth-checkbox-label">
              I agree to the{' '}
              <a
                href={`${BARE_DOMAIN}/legal?tab=terms`}
                target="_blank"
                rel="noreferrer"
                className="sl-auth-checkbox-link"
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a
                href={`${BARE_DOMAIN}/legal?tab=privacy`}
                target="_blank"
                rel="noreferrer"
                className="sl-auth-checkbox-link"
              >
                Privacy Policy
              </a>
            </span>
          </label>

          <label className="sl-auth-checkbox">
            <input
              type="checkbox"
              checked={ageChecked}
              onChange={(e) => setAgeChecked(e.target.checked)}
              disabled={isSubmitting}
            />
            <span className="sl-auth-checkbox-mark" />
            <span className="sl-auth-checkbox-label">
              I confirm that I am at least 16 years of age
            </span>
          </label>
        </div>

        {error && (
          <div className="sl-auth-error" style={{ marginTop: '12px' }}>
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
